// Apple HealthKit sync — iOS native shell only.
//
// Reads today's HRV, resting heart rate, sleep, and steps through
// @capgo/capacitor-health and posts them to /healthkit/sync, which
// upserts one wearable_daily row per (user, day, source='healthkit').
//
// The plugin is imported STATICALLY on purpose. It used to be a
// dynamic import() to keep Capacitor code out of the web bundle, but
// that made it the app's only code-split chunk — and on physical
// devices that import() stalled forever inside the WKWebView (the
// Build 6 iPad hang Apple hit, and the Build 8 iPhone "plugin did not
// respond (15s)" timeout). Riding in the main bundle — which provably
// loads — removes the failure mode outright; the cost is ~9 kB on the
// web bundle, where registerPlugin() at module scope is inert and
// every call below stays behind isNativeIOS().
//
// HealthKit never discloses read-permission grants (an app can't tell
// "denied" from "no data"). Completing the permission sheet therefore
// proves nothing: iOS resolves requestAuthorization identically for
// Allow and Don't Allow, and suppresses the sheet entirely once the
// types are determined (so a previously-denied user sees no UI at all).
//
// "Connected" is therefore evidence-based: the flag is set only once a
// sync has actually posted a sample to the backend, which is the one
// observable that requires a real grant. Between completing the sheet
// and the first sample landing the state is `pending`, so the UI can
// say "waiting for data" instead of claiming a connection it can't
// substantiate. The server (`GET /wearables/status`) remains the
// authority; these flags are the offline/optimistic mirror.

import * as Sentry from '@sentry/react'
import { Health } from '@capgo/capacitor-health'

import { isNativeIOS } from '../utils/platform'
import { healthkitApi } from '../api/healthkit'

const CONNECTED_KEY = 'haiven_healthkit_connected'
// Set when the user completes the permission flow, cleared once data
// actually lands. "Pending" is the honest state for the window where we
// have asked but cannot yet prove a grant.
const PENDING_KEY = 'haiven_healthkit_pending'
const LAST_SYNC_KEY = 'haiven_healthkit_last_sync'
const SYNC_MIN_INTERVAL_MS = 60 * 60 * 1000 // at most one background sync/hour

const READ_TYPES = ['heartRateVariability', 'restingHeartRate', 'sleep', 'steps']

// Sleep sample states that count toward time asleep ('inBed' and
// 'awake' segments don't).
const ASLEEP_STATES = new Set(['asleep', 'rem', 'deep', 'light'])

export function isHealthKitSupported() {
  return isNativeIOS()
}

export function isHealthKitConnected() {
  try {
    return localStorage.getItem(CONNECTED_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * True when the user has completed the permission flow but no sample
 * has landed yet. Not the same as connected — Apple Health may simply
 * have nothing for us, or the user may have denied the read.
 */
export function isHealthKitPending() {
  try {
    return localStorage.getItem(PENDING_KEY) === 'true' &&
      localStorage.getItem(CONNECTED_KEY) !== 'true'
  } catch {
    return false
  }
}

// Promote pending → connected. Called only from the sync path, on
// evidence that at least one sample reached the backend.
function markHealthKitConnected() {
  try {
    localStorage.setItem(CONNECTED_KEY, 'true')
    localStorage.removeItem(PENDING_KEY)
  } catch { /* private mode — server status still governs the UI */ }
}

/**
 * Drop all local HealthKit connection state. Called on sign-out: the
 * flags are device-scoped, not user-scoped, so without this they leak
 * across account switches on a shared device.
 */
export function clearHealthKitConnection() {
  try {
    localStorage.removeItem(CONNECTED_KEY)
    localStorage.removeItem(PENDING_KEY)
    localStorage.removeItem(LAST_SYNC_KEY)
  } catch { /* private mode — nothing was persisted anyway */ }
}

export function healthKitLastSyncedAt() {
  try {
    return localStorage.getItem(LAST_SYNC_KEY) || null
  } catch {
    return null
  }
}

// Local calendar date (YYYY-MM-DD) — same contract as the check-in
// upsert, so the row buckets against the user's day, not UTC.
function localDateString(d = new Date()) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// Local midnight `offset` days before today (offset 0 = today 00:00).
function localMidnight(offset = 0) {
  const d = new Date()
  d.setDate(d.getDate() - offset)
  d.setHours(0, 0, 0, 0)
  return d
}

// ── Origin-tagged readers ─────────────────────────────────────────────
//
// HealthKit is a pipe, not a device: Garmin, Oura, and Apple Watch all
// pour samples into it. Every reader below uses readSamples (never
// queryAggregated — aggregated buckets merge all sources with no
// provenance, and the plugin hard-rejects aggregated HRV outright) and
// groups per (origin, local day), so each true device becomes its own
// wearable_daily row (source "healthkit:<origin>") for the backend
// resolution layer to rank.

// Map a sample's HKSource to a stable origin slug.
function originSlug(sample) {
  const id = (sample.sourceId || '').toLowerCase()
  const name = (sample.sourceName || '').toLowerCase()
  if (id.startsWith('com.ouraring') || name.includes('oura')) return 'oura'
  if (id.startsWith('com.garmin') || name.includes('garmin')) return 'garmin'
  if (id.startsWith('com.fitbit') || name.includes('fitbit')) return 'fitbit'
  if (id.startsWith('com.apple.health')) {
    return name.includes('watch') ? 'apple_watch' : 'iphone'
  }
  const slug = name.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  return (slug || 'other').slice(0, 20)
}

async function readAll(dataType, windowStart, limit) {
  try {
    const { samples } = await Health.readSamples({
      dataType,
      startDate: windowStart.toISOString(),
      endDate: new Date().toISOString(),
      limit,
      ascending: true,
    })
    return samples || []
  } catch {
    return [] // type denied / no data — partial rows are fine
  }
}

// {origin: {day: avg(value)}} — HRV (SDNN ms) and resting HR.
async function averageByOriginDay(dataType, windowStart, limit = 1000) {
  const sums = {}
  for (const s of await readAll(dataType, windowStart, limit)) {
    if (!Number.isFinite(s.value)) continue
    const origin = originSlug(s)
    const day = localDateString(new Date(s.startDate))
    const acc = (sums[origin] ||= {})[day] ||= { total: 0, n: 0 }
    acc.total += s.value
    acc.n += 1
  }
  const out = {}
  for (const [origin, days] of Object.entries(sums)) {
    out[origin] = {}
    for (const [day, { total, n }] of Object.entries(days)) {
      out[origin][day] = Math.round((total / n) * 10) / 10
    }
  }
  return out
}

// {origin: {day: sum(value)}} — steps.
async function sumByOriginDay(dataType, windowStart, limit = 5000) {
  const out = {}
  for (const s of await readAll(dataType, windowStart, limit)) {
    if (!Number.isFinite(s.value)) continue
    const origin = originSlug(s)
    const day = localDateString(new Date(s.startDate))
    const days = (out[origin] ||= {})
    days[day] = (days[day] || 0) + s.value
  }
  return out
}

// {origin: {day: hours}} — time asleep as the UNION of sleep intervals.
// Overlapping segments (Garmin and Oura describing the same night, or
// stage-level rows inside one source) are merged to wall-clock time,
// never summed — summing is how a real 15.26h "night" reached prod.
// A segment belongs to the day it ENDS on when that's before 18:00
// (overnight sleep ends that morning), else the following day.
async function sleepByOriginDay(windowStart) {
  const fetchStart = new Date(windowStart)
  fetchStart.setDate(fetchStart.getDate() - 1)
  fetchStart.setHours(18, 0, 0, 0)
  const intervals = {}
  for (const s of await readAll('sleep', fetchStart, 2000)) {
    if (s.sleepState && !ASLEEP_STATES.has(s.sleepState)) continue
    const startMs = new Date(s.startDate).getTime()
    const endMs = new Date(s.endDate).getTime()
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) continue
    const end = new Date(s.endDate)
    if (end.getHours() >= 18) end.setDate(end.getDate() + 1)
    const day = localDateString(end)
    const origin = originSlug(s)
    ;((intervals[origin] ||= {})[day] ||= []).push([startMs, endMs])
  }
  const out = {}
  for (const [origin, days] of Object.entries(intervals)) {
    out[origin] = {}
    for (const [day, segs] of Object.entries(days)) {
      segs.sort((a, b) => a[0] - b[0])
      let ms = 0
      let [curStart, curEnd] = segs[0]
      for (const [s0, e0] of segs.slice(1)) {
        if (s0 <= curEnd) {
          curEnd = Math.max(curEnd, e0)
        } else {
          ms += curEnd - curStart
          curStart = s0
          curEnd = e0
        }
      }
      ms += curEnd - curStart
      out[origin][day] = Math.round((ms / 3_600_000) * 100) / 100
    }
  }
  return out
}

// Reject if ``promise`` hasn't settled within ``ms``. A stalled plugin
// call NEVER rejects on its own, so without this a stall is invisible:
// the button spins forever and Sentry (which only sees rejections)
// records nothing. Timeouts convert every stall into a captured,
// step-tagged error.
function withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} did not respond (${Math.round(ms / 1000)}s)`)),
      ms,
    )
    promise.then(
      (value) => { clearTimeout(timer); resolve(value) },
      (err) => { clearTimeout(timer); reject(err) },
    )
  })
}

// Run the iOS permission flow and mark the integration connected.
// Returns true on success; throws with a user-facing message otherwise.
//
// Two hard-won iOS facts shape this flow:
//
//   1. iOS presents the HealthKit permission sheet AT MOST ONCE per
//      app + data-type set — for the lifetime of the device, allow or
//      deny, surviving reinstalls. Every later requestAuthorization
//      resolves silently with no UI. So we pre-flight with
//      checkAuthorization (non-interactive): if no type still needs
//      requesting, we skip the request entirely instead of awaiting a
//      sheet that will never appear. (Users who denied re-enable via
//      the Health app → Profile → Privacy → Apps → Haiven.)
//   2. The first sync is fire-and-forget. It POSTs to the Render free
//      tier — a cold start plus the api client's retry backoff held
//      the button on "Connecting…" for minutes. Permission is the
//      connect; data lands when it lands (Wearables has "Sync now").
//
// Every failure is captured to Sentry with a step tag before the
// rethrow, and each step drops a breadcrumb — so a timeout event shows
// exactly how far the flow got.
export async function connectHealthKit(token) {
  if (!isNativeIOS()) {
    throw new Error('Apple Health is available in the Haiven iOS app.')
  }
  const crumb = (name) =>
    Sentry.addBreadcrumb({ category: 'healthkit', message: `connect:${name}` })
  const step = (name) => (err) => {
    Sentry.captureException(
      err instanceof Error ? err : new Error(String(err?.message || err)),
      { tags: { integration: 'healthkit', step: name } },
    )
    throw err
  }

  crumb('is_available')
  const availability = await withTimeout(
    Health.isAvailable(), 10_000, 'The Apple Health availability check',
  ).catch(step('is_available'))
  if (!availability?.available) {
    Sentry.captureMessage('HealthKit unavailable on device', {
      level: 'warning',
      tags: { integration: 'healthkit', step: 'is_available' },
      extra: { reason: availability?.reason, platform: availability?.platform },
    })
    throw new Error('Apple Health is not available on this device.')
  }

  // Pre-flight (best-effort): readDenied lists the types iOS would
  // still show a sheet for. Empty ⇒ everything is already determined
  // and requestAuthorization would be a silent no-op — skip it.
  crumb('check_authorization')
  let needsRequest = true
  try {
    const status = await withTimeout(
      Health.checkAuthorization({ read: READ_TYPES }),
      10_000, 'The Apple Health permission check',
    )
    needsRequest = (status?.readDenied || []).length > 0
  } catch { /* fall through to the request — worst case a silent resolve */ }

  if (needsRequest) {
    crumb('request_authorization')
    // Generous timeout: the sheet is interactive and the user may read
    // it. If it fires, the sheet never presented (the stall this build
    // fixes) — and the flow self-heals: once iOS marks the types
    // determined, the next tap skips the request via the pre-flight.
    await withTimeout(
      Health.requestAuthorization({ read: READ_TYPES }),
      120_000, 'The Apple Health permission sheet',
    ).catch(step('request_authorization'))
  }

  // Pending, not connected: the sheet above resolves the same way for
  // Allow and Don't Allow, and may not have presented at all. Only a
  // sample actually reaching the backend promotes this to connected —
  // see markHealthKitConnected in the sync path.
  try {
    localStorage.setItem(PENDING_KEY, 'true')
  } catch { /* private mode — sync still works this session */ }

  // First sync in the background — never blocks the connect button.
  // Wider window on connect so the last week of history lands at once.
  crumb('first_sync_started')
  syncHealthKitToday(token, { force: true, days: CONNECT_SYNC_DAYS }).catch(() => {})
  return true
}

// Sync window sizes. Multi-day, because Apple publishes some daily
// values late (resting HR often lands mid/late day; sleep finalizes
// the next morning) — a today-only sync permanently missed whatever
// arrived after the app's last foreground of the day. Re-posting
// recent days converges: the backend upserts per (user, date, source)
// and never overwrites a known value with null.
const DEFAULT_SYNC_DAYS = 3
const CONNECT_SYNC_DAYS = 7

// Single-flight: connect's first sync and the foreground-resume sync
// can fire seconds apart (the permission sheet dismissal itself
// triggers visibilitychange), and both pass the throttle check before
// either finishes to stamp it — the Build 9 double-write. Concurrent
// callers now share one run.
let inFlightSync = null

// Read the last `days` days of metrics and post one payload per day.
// Never throws on the background path (app resume) — a failed read or
// POST just tries again next time.
export async function syncHealthKitToday(token, { force = false, days = DEFAULT_SYNC_DAYS } = {}) {
  // Pending counts: it is the state between completing the permission
  // flow and the first sample landing, and this sync is exactly what
  // resolves it. Gating on connected alone would deadlock — connected
  // is earned by a successful sync.
  if (!isNativeIOS() || !token) return false
  if (!isHealthKitConnected() && !isHealthKitPending()) return false
  if (!force) {
    const last = healthKitLastSyncedAt()
    if (last && Date.now() - new Date(last).getTime() < SYNC_MIN_INTERVAL_MS) {
      return false
    }
  }
  if (inFlightSync) return inFlightSync
  inFlightSync = runSync(token, { force, days }).finally(() => { inFlightSync = null })
  return inFlightSync
}

async function runSync(token, { force, days }) {
  try {
    const windowStart = localMidnight(days - 1)
    const [hrv, restingHr, steps, sleep] = await Promise.all([
      // HealthKit reports HRV as SDNN (ms); the resolution layer ranks
      // it below direct-integration HRV anyway.
      averageByOriginDay('heartRateVariability', windowStart),
      averageByOriginDay('restingHeartRate', windowStart),
      sumByOriginDay('steps', windowStart),
      sleepByOriginDay(windowStart),
    ])

    const origins = new Set([
      ...Object.keys(hrv), ...Object.keys(restingHr),
      ...Object.keys(steps), ...Object.keys(sleep),
    ])

    // One POST per (day, origin), oldest day first so a mid-loop
    // failure leaves the freshest data for the next attempt.
    const sent = []
    for (let offset = days - 1; offset >= 0; offset--) {
      const day = localDateString(localMidnight(offset))
      for (const origin of origins) {
        const payload = {
          date: day,
          hrv_rmssd: hrv[origin]?.[day] ?? null,
          resting_heart_rate: restingHr[origin]?.[day] ?? null,
          steps: steps[origin]?.[day] ?? null,
          sleep_duration_hours: sleep[origin]?.[day] ?? null,
          source: `healthkit:${origin}`,
        }
        const fields = {
          hrv: payload.hrv_rmssd != null,
          resting_hr: payload.resting_heart_rate != null,
          steps: payload.steps != null,
          sleep: payload.sleep_duration_hours != null,
        }
        // Mandatory observability: which types each ORIGIN returned —
        // "no Watch hardware" (garmin/oura relays, no hrv, forever) vs
        // "read path broken" (nothing at all) diagnosable remotely.
        Sentry.addBreadcrumb({
          category: 'healthkit',
          message: `sync:${day}:${origin}`,
          data: fields,
        })
        if (!fields.hrv && !fields.resting_hr && !fields.steps && !fields.sleep) {
          continue // nothing from this origin that day
        }
        await healthkitApi.sync(payload, token)
        sent.push({ date: day, origin, ...fields })
      }
    }

    if (force) {
      // User-initiated syncs emit a summary event so per-origin,
      // per-type coverage is visible in Sentry (breadcrumbs only ship
      // attached to events).
      Sentry.captureMessage('HealthKit sync summary', {
        level: 'info',
        tags: { integration: 'healthkit', step: 'sync_summary' },
        extra: { days: sent, window_days: days },
      })
    }

    if (sent.length === 0) return false
    // A sample reached the backend — that is the only observable that
    // requires a real read grant, so this is where "connected" is earned.
    markHealthKitConnected()
    try {
      localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString())
    } catch { /* private mode */ }
    return true
  } catch (err) {
    // User-initiated syncs (connect's first sync, "Sync now") report to
    // Sentry; the hourly background path stays quiet — offline resumes
    // would drown the signal.
    if (force) {
      Sentry.captureException(
        err instanceof Error ? err : new Error(String(err?.message || err)),
        { tags: { integration: 'healthkit', step: 'sync' } },
      )
    }
    return false
  }
}
